import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { 
  Heart, MessageCircle, Share2, Plus, X, MapPin, Calendar,
  Plane, Coffee, Mountain, Camera, BadgeCheck, ArrowLeft
} from "lucide-react";
import HavnsLogo from "../components/HavnsLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sample stories for demo
const sampleStories = [
  {
    story_id: "1",
    author: {
      name: "Priya",
      profession: "Architect",
      photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=faces",
      is_verified: true,
    },
    type: "travel",
    title: "48 Hours in Kyoto",
    content: "Woke up at 5am to catch the sunrise at Fushimi Inari. The thousands of torii gates were completely empty - just me, the morning mist, and the sound of birds. This is why I travel solo. No compromises, just pure moments of magic. Met two fellow soloists at a tiny ramen shop that evening - turns out we were all staying in the same neighborhood. We ended up exploring Gion together the next day. Solo travel doesn't mean lonely travel. 🏯",
    image: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&h=600&fit=crop",
    location: "Kyoto, Japan",
    date: "2 days ago",
    likes: 47,
    comments: 12,
  },
  {
    story_id: "2",
    author: {
      name: "Arjun",
      profession: "Tech Lead",
      photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=faces",
      is_verified: true,
    },
    type: "meetup",
    title: "Sunday Morning Trail Run",
    content: "Posted on Havns looking for trail running buddies. Expected maybe 2-3 people. Showed up to find 8 fellow soloists ready to hit the Cubbon Park trails! We did 10k, grabbed filter coffee after, and now have a standing Sunday morning run club. This is what I love about this community - high-intent people who actually show up. Next week we're planning a sunrise run at Nandi Hills. Who's in?",
    image: "https://images.unsplash.com/photo-1571008887538-b36bb32f4571?w=800&h=600&fit=crop",
    location: "Bangalore",
    date: "5 days ago",
    likes: 89,
    comments: 24,
  },
  {
    story_id: "3",
    author: {
      name: "Meera",
      profession: "Documentary Filmmaker",
      photo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=faces",
      is_verified: false,
    },
    type: "travel",
    title: "Finding Silence in Spiti",
    content: "Three weeks in Spiti Valley. No phone signal for days. Just me, my camera, and the most stunning landscapes I've ever seen. Stayed with a local family in Langza who taught me to make traditional butter tea. The stars at night? Unreal. Sometimes the best travel companion is yourself. Coming back with 200 hours of footage and a completely different perspective on life. Already planning my return trip - any fellow soloists interested in a photography expedition?",
    image: "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?w=800&h=600&fit=crop",
    location: "Spiti Valley, Himachal",
    date: "1 week ago",
    likes: 156,
    comments: 38,
  },
  {
    story_id: "4",
    author: {
      name: "Vikram",
      profession: "Venture Partner",
      photo: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=faces",
      is_verified: true,
    },
    type: "meetup",
    title: "Jazz Night at Blue Frog",
    content: "Organized a Havns jazz night last Friday. What started as 'let's grab drinks and listen to live jazz' turned into a 4-hour conversation about everything from startup culture to the best vinyl shops in Mumbai. Connected with a fellow soloist who shares my obsession with 70s jazz fusion - we're now planning a trip to the Montreux Jazz Festival next year. This community gets it. Quality over quantity, always.",
    image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=800&h=600&fit=crop",
    location: "Mumbai",
    date: "3 days ago",
    likes: 72,
    comments: 18,
  },
  {
    story_id: "5",
    author: {
      name: "Ananya",
      profession: "Product Designer",
      photo: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=faces",
      is_verified: true,
    },
    type: "travel",
    title: "Solo Diving in Andamans",
    content: "Finally did it - got my Advanced Open Water certification! Spent a week in Havelock diving every single day. The underwater world is insane. Spotted manta rays, swam through schools of fish, and had the most peaceful moments of my year. The best part? Met three other solo divers at the dive center. We formed an impromptu group, shared meals, exchanged stories, and promised to reunite for a Raja Ampat trip. Solo travel = meeting your tribe.",
    image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&h=600&fit=crop",
    location: "Havelock Island, Andamans",
    date: "1 week ago",
    likes: 134,
    comments: 29,
  },
];

const Stories = () => {
  const navigate = useNavigate();
  const [stories, setStories] = useState(sampleStories);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState("all"); // all, travel, meetup
  const [likedStories, setLikedStories] = useState(new Set());

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${API}/stories`);
      if (response.data.length > 0) {
        setStories(response.data);
      }
    } catch (error) {
      console.log("Using sample stories");
    }
  };

  const handleLike = (storyId) => {
    if (likedStories.has(storyId)) {
      likedStories.delete(storyId);
      setLikedStories(new Set(likedStories));
    } else {
      setLikedStories(new Set(likedStories.add(storyId)));
    }
  };

  const filteredStories = filter === "all" 
    ? stories 
    : stories.filter(s => s.type === filter);

  const getTypeIcon = (type) => {
    switch (type) {
      case "travel": return <Plane className="w-4 h-4" />;
      case "meetup": return <Coffee className="w-4 h-4" />;
      default: return <Mountain className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F7]">
      {/* Header */}
      <header className="border-b border-[#EEE] bg-[#FBF9F7]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-[#666] hover:text-[#1A1A1A]">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <HavnsLogo className="text-xl" linkTo="/" />
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-4 py-2 text-sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Share Story
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <h1 className="font-serif text-3xl text-[#1A1A1A] mb-2">Soloist Stories</h1>
          <p className="text-[#666]">Adventures, meetups, and moments from our community</p>
        </div>

        {/* Filters */}
        <div className="flex justify-center gap-2 mb-8">
          {[
            { key: "all", label: "All Stories" },
            { key: "travel", label: "Travel", icon: <Plane className="w-4 h-4" /> },
            { key: "meetup", label: "Meetups", icon: <Coffee className="w-4 h-4" /> },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full text-sm flex items-center gap-2 transition-all ${
                filter === f.key
                  ? "bg-[#1A1A1A] text-white"
                  : "bg-white text-[#666] hover:bg-[#F5F5F5]"
              }`}
            >
              {f.icon}
              {f.label}
            </button>
          ))}
        </div>

        {/* Stories Feed */}
        <div className="space-y-6">
          {filteredStories.map((story) => (
            <article 
              key={story.story_id} 
              className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Author Header */}
              <div className="p-4 flex items-center gap-3">
                <img
                  src={story.author.photo}
                  alt={story.author.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[#1A1A1A]">{story.author.name}</span>
                    {story.author.is_verified && (
                      <BadgeCheck className="w-4 h-4 text-[#C5B358]" />
                    )}
                  </div>
                  <span className="text-sm text-[#999]">{story.author.profession}</span>
                </div>
                <div className="flex items-center gap-1 px-3 py-1 bg-[#FBF9F7] rounded-full text-xs text-[#666]">
                  {getTypeIcon(story.type)}
                  <span className="capitalize">{story.type}</span>
                </div>
              </div>

              {/* Image */}
              {story.image && (
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={story.image}
                    alt={story.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Content */}
              <div className="p-5">
                <h2 className="font-serif text-xl text-[#1A1A1A] mb-2">{story.title}</h2>
                <p className="text-[#666] leading-relaxed whitespace-pre-line">{story.content}</p>
                
                {/* Meta */}
                <div className="flex items-center gap-4 mt-4 text-sm text-[#999]">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {story.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {story.date}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="px-5 pb-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button 
                    onClick={() => handleLike(story.story_id)}
                    className={`flex items-center gap-2 transition-colors ${
                      likedStories.has(story.story_id) 
                        ? "text-red-500" 
                        : "text-[#666] hover:text-red-500"
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${likedStories.has(story.story_id) ? "fill-current" : ""}`} />
                    <span className="text-sm">{story.likes + (likedStories.has(story.story_id) ? 1 : 0)}</span>
                  </button>
                  <button className="flex items-center gap-2 text-[#666] hover:text-[#1A1A1A] transition-colors">
                    <MessageCircle className="w-5 h-5" />
                    <span className="text-sm">{story.comments}</span>
                  </button>
                </div>
                <button className="text-[#666] hover:text-[#1A1A1A] transition-colors">
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* Empty State */}
        {filteredStories.length === 0 && (
          <div className="text-center py-16">
            <p className="text-[#999]">No stories yet. Be the first to share!</p>
          </div>
        )}
      </main>

      {/* Create Story Modal */}
      {showCreateModal && (
        <CreateStoryModal onClose={() => setShowCreateModal(false)} onSuccess={fetchStories} />
      )}
    </div>
  );
};

// Create Story Modal Component
const CreateStoryModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    type: "travel",
    title: "",
    content: "",
    location: "",
    image: null,
  });
  const [loading, setLoading] = useState(false);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({ ...prev, image: e.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) {
      toast.error("Please add a title and story");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/stories`, formData);
      toast.success("Story shared!");
      onSuccess();
      onClose();
    } catch (error) {
      toast.error("Failed to share story");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#EEE] flex items-center justify-between">
          <h2 className="font-serif text-xl text-[#1A1A1A]">Share Your Story</h2>
          <button onClick={onClose} className="text-[#999] hover:text-[#1A1A1A]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Type Selection */}
          <div className="flex gap-2">
            {[
              { key: "travel", label: "Travel Story", icon: <Plane className="w-4 h-4" /> },
              { key: "meetup", label: "Meetup Story", icon: <Coffee className="w-4 h-4" /> },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setFormData(prev => ({ ...prev, type: t.key }))}
                className={`flex-1 py-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                  formData.type === t.key
                    ? "bg-[#1A1A1A] text-white"
                    : "bg-[#F5F5F5] text-[#666]"
                }`}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>

          {/* Title */}
          <input
            type="text"
            placeholder="Give your story a title..."
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-4 py-3 rounded-xl border border-[#EEE] focus:border-[#1A1A1A] outline-none"
          />

          {/* Content */}
          <Textarea
            placeholder="Share your adventure, experience, or meetup story..."
            value={formData.content}
            onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
            className="min-h-[150px] rounded-xl border-[#EEE] focus:border-[#1A1A1A] resize-none"
          />

          {/* Location */}
          <div className="flex items-center gap-2 px-4 py-3 bg-[#F5F5F5] rounded-xl">
            <MapPin className="w-5 h-5 text-[#999]" />
            <input
              type="text"
              placeholder="Add location..."
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              className="flex-1 bg-transparent outline-none"
            />
          </div>

          {/* Image Upload */}
          <div>
            {formData.image ? (
              <div className="relative rounded-xl overflow-hidden">
                <img src={formData.image} alt="Story" className="w-full h-48 object-cover" />
                <button
                  onClick={() => setFormData(prev => ({ ...prev, image: null }))}
                  className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 py-8 border-2 border-dashed border-[#DDD] rounded-xl cursor-pointer hover:border-[#999] transition-colors">
                <Camera className="w-5 h-5 text-[#999]" />
                <span className="text-[#999]">Add a photo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-[#EEE]">
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full py-6"
          >
            {loading ? "Sharing..." : "Share Story"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Stories;
